/**
 * HTTP Request View
 * Interactive HTTP request tool
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import { UtilsService, HttpResponse } from '../../../services/utils.service.js';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export const HttpView: React.FC = () => {
    const [url, setUrl] = useState('');
    const [method, setMethod] = useState<string>('GET');
    const [headers, setHeaders] = useState('');
    const [body, setBody] = useState('');
    const [response, setResponse] = useState<HttpResponse | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [focusedField, setFocusedField] = useState<'url' | 'method' | 'headers' | 'body' | 'send'>('url');
    const [copied, setCopied] = useState(false);

    const service = new UtilsService();

    useInput((input, key) => {
        if (loading) return;

        if (key.tab) {
            const fields: typeof focusedField[] = ['url', 'method', 'headers', 'body', 'send'];
            const currentIndex = fields.indexOf(focusedField);
            setFocusedField(fields[(currentIndex + 1) % fields.length]);
        } else if (key.leftArrow && focusedField === 'method') {
            const currentIndex = METHODS.indexOf(method);
            setMethod(METHODS[currentIndex > 0 ? currentIndex - 1 : METHODS.length - 1]);
        } else if (key.rightArrow && focusedField === 'method') {
            const currentIndex = METHODS.indexOf(method);
            setMethod(METHODS[(currentIndex + 1) % METHODS.length]);
        } else if (key.return && focusedField === 'send') {
            handleSend();
        } else if (input === 'c' && response) {
            handleCopy();
        }
    });

    const handleSend = async () => {
        if (!url) {
            setError('Please enter a URL');
            return;
        }

        try {
            setError('');
            setLoading(true);

            const headersObj: Record<string, string> = {};
            if (headers) {
                const headerLines = headers.split('\n');
                for (const line of headerLines) {
                    const [key, ...valueParts] = line.split(':');
                    if (key && valueParts.length > 0) {
                        headersObj[key.trim()] = valueParts.join(':').trim();
                    }
                }
            }

            const result = await service.httpRequest(url, {
                method,
                headers: headersObj,
                body: body || undefined,
            });

            setResponse(result);
            setCopied(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Request failed');
            setResponse(null);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async () => {
        if (!response) return;
        try {
            const { default: clipboardy } = await import('clipboardy');
            const copyText = typeof response.body === 'string'
                ? response.body
                : JSON.stringify(response.body, null, 2);
            await clipboardy.write(copyText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Clipboard not available
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    🌐 HTTP Request
                </Text>
            </Box>

            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Request:
                </Text>

                <Box marginBottom={1} flexDirection="column">
                    <Box marginBottom={0}>
                        <Text color={focusedField === 'url' ? 'green' : undefined}>
                            {focusedField === 'url' ? '▶ ' : '  '}URL:
                        </Text>
                    </Box>
                    <Box marginLeft={2} width={60}>
                        {focusedField === 'url' ? (
                            <TextInput
                                value={url}
                                onChange={setUrl}
                                placeholder="https://api.example.com/endpoint"
                            />
                        ) : (
                            <Text dimColor={!url}>{url || '(empty)'}</Text>
                        )}
                    </Box>
                </Box>

                <Box marginBottom={1}>
                    <Box width={20}>
                        <Text color={focusedField === 'method' ? 'green' : undefined}>
                            {focusedField === 'method' ? '▶ ' : '  '}Method:
                        </Text>
                    </Box>
                    <Text bold color={focusedField === 'method' ? 'yellow' : undefined}>
                        {method}
                    </Text>
                    {focusedField === 'method' && (
                        <Text dimColor> (← → to change)</Text>
                    )}
                </Box>

                <Box marginBottom={1} flexDirection="column">
                    <Box marginBottom={0}>
                        <Text color={focusedField === 'headers' ? 'green' : undefined}>
                            {focusedField === 'headers' ? '▶ ' : '  '}Headers (one per line, Key: Value):
                        </Text>
                    </Box>
                    <Box marginLeft={2} width={60}>
                        {focusedField === 'headers' ? (
                            <TextInput
                                value={headers}
                                onChange={setHeaders}
                                placeholder="Authorization: Bearer token"
                            />
                        ) : (
                            <Text dimColor={!headers}>{headers || '(none)'}</Text>
                        )}
                    </Box>
                </Box>

                {(method === 'POST' || method === 'PUT' || method === 'PATCH') && (
                    <Box marginBottom={1} flexDirection="column">
                        <Box marginBottom={0}>
                            <Text color={focusedField === 'body' ? 'green' : undefined}>
                                {focusedField === 'body' ? '▶ ' : '  '}Body (JSON):
                            </Text>
                        </Box>
                        <Box marginLeft={2} width={60}>
                            {focusedField === 'body' ? (
                                <TextInput
                                    value={body}
                                    onChange={setBody}
                                    placeholder='{"key":"value"}'
                                />
                            ) : (
                                <Text dimColor={!body}>{body || '(empty)'}</Text>
                            )}
                        </Box>
                    </Box>
                )}

                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'send' ? 'green' : undefined}
                        color={focusedField === 'send' ? 'black' : 'green'}
                    >
                        {focusedField === 'send' ? '▶ ' : '  '}
                        [ {loading ? 'Sending...' : 'Send Request'} ]
                    </Text>
                </Box>
            </Box>

            {error && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="red"
                    paddingX={2}
                    paddingY={1}
                    marginBottom={1}
                >
                    <Text color="red">✗ Error: {error}</Text>
                </Box>
            )}

            {response && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Box marginBottom={1}>
                        <Text bold color="green">
                            ✓ Response:
                        </Text>
                        {copied && (
                            <Box marginLeft={2}>
                                <Text color="green">✓ Copied body to clipboard!</Text>
                            </Box>
                        )}
                    </Box>

                    <Box flexDirection="column">
                        <Text>
                            Status: <Text color={response.status < 400 ? 'green' : 'red'}>{response.status}</Text>
                        </Text>
                        <Text>Time: {response.time}ms</Text>
                        <Text>Size: {response.size}</Text>
                        <Text> </Text>

                        <Text bold color="yellow">Headers:</Text>
                        {Object.entries(response.headers).slice(0, 5).map(([key, value]) => (
                            <Text key={key} dimColor>  {key}: {value}</Text>
                        ))}
                        <Text> </Text>

                        <Text bold color="yellow">Body:</Text>
                        <Text>
                            {typeof response.body === 'string'
                                ? response.body.slice(0, 500)
                                : JSON.stringify(response.body, null, 2).slice(0, 500)}
                        </Text>
                    </Box>
                </Box>
            )}

            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next | ←/→: Change method | Enter: Send | C: Copy body | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
